import React, { useState, useEffect } from 'react';
import { clientApi } from '../lib/clientAuth';
import { Search, List, LayoutGrid, Calendar as CalendarIcon, ChevronLeft, ChevronRight, CheckCircle, Clock, AlertTriangle, TrendingUp, CalendarDays, User, Eye } from 'lucide-react';
import TaskFilters from './TaskFilters';
import EmptyState from './EmptyState';

// Helper: prefer use_date (when content goes live for client) over due_date
const clientPublishDate = (task) => task.use_date || task.due_date;

// Task Statistics Component (view-only)
function TaskStatistics({ tasks }) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const tasksThisMonth = tasks.filter(task => { const dStr = clientPublishDate(task); if (!dStr) return false; const d = new Date(dStr); return d.getMonth() === currentMonth && d.getFullYear() === currentYear; });
  const completed = tasksThisMonth.filter(t => t.status === 'Completed').length;
  const pending = tasksThisMonth.filter(t => t.status !== 'Completed').length;
  const overdue = tasks.filter(t => { const dStr = clientPublishDate(t); if (!dStr || t.status === 'Completed') return false; const d = new Date(dStr); const today = new Date(); today.setHours(0,0,0,0); return d < today; }).length;
  const highPriority = tasks.filter(t => (t.priority === 'High' || t.priority === 'Critical') && t.status !== 'Completed').length;

  return (
    <div className="grid grid-cols-5 gap-4 mb-6" data-testid="task-statistics">
      <div className="glass-card p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center"><CalendarIcon className="w-5 h-5 text-blue-600" /></div><div><p className="text-sm text-neutral-500">Tasks This Month</p><p className="text-2xl font-bold text-[#1a1a2e] dark:text-white">{tasksThisMonth.length}</p></div></div></div>
      <div className="glass-card p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600" /></div><div><p className="text-sm text-neutral-500">Completed</p><p className="text-2xl font-bold text-green-600">{completed}</p></div></div></div>
      <div className="glass-card p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-yellow-100 dark:bg-yellow-500/20 flex items-center justify-center"><Clock className="w-5 h-5 text-yellow-600" /></div><div><p className="text-sm text-neutral-500">Pending</p><p className="text-2xl font-bold text-yellow-600">{pending}</p></div></div></div>
      <div className="glass-card p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div><div><p className="text-sm text-neutral-500">Overdue</p><p className="text-2xl font-bold text-red-600">{overdue}</p></div></div></div>
      <div className="glass-card p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-orange-600" /></div><div><p className="text-sm text-neutral-500">High Priority</p><p className="text-2xl font-bold text-orange-600">{highPriority}</p></div></div></div>
    </div>
  );
}

// Read-only Task Row
function TaskRow({ task, statusColors, priorityColors, onView }) {
  return (
    <tr className="hover:bg-white/60 dark:hover:bg-white/5 transition-colors group" data-testid={`task-row-${task._id}`}>
      <td className="px-4 py-3">
        <span className="font-semibold text-[#1a1a2e] dark:text-white">{task.title || '-'}</span>
        {task.description && <p className="text-sm text-neutral-500 mt-1 truncate max-w-xs">{task.description}</p>}
      </td>
      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">{task.task_type || '-'}</td>
      <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${priorityColors[task.priority] || 'bg-gray-200 text-gray-700'}`}>{task.priority || '-'}</span></td>
      <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[task.status] || 'bg-gray-200 text-gray-700'}`}>{task.status || '-'}</span></td>
      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400" title="Publish date (when your content goes live)">{clientPublishDate(task) || '-'}</td>
      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">{task.assignee_name || 'Unassigned'}</td>
      <td className="px-4 py-3">
        <button onClick={() => onView(task)} className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="View Details" data-testid={`view-task-${task._id}`}>
          <Eye className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

// Read-only Kanban Card
function KanbanCard({ task, onView }) {
  return (
    <div className="glass-card p-4 cursor-pointer hover:shadow-soft" onClick={() => onView(task)} data-testid={`kanban-card-${task._id}`}>
      <h4 className="font-semibold text-[#1a1a2e] dark:text-white mb-2 line-clamp-2">{task.title}</h4>
      {task.description && <p className="text-sm text-neutral-500 mb-3 line-clamp-2">{task.description}</p>}
      <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1"><User className="w-3 h-3" /><span>{task.assignee_name || 'Unassigned'}</span></div>
      {clientPublishDate(task) && <div className="flex items-center gap-2 text-xs text-neutral-500" title="Publish date"><CalendarDays className="w-3 h-3" /><span>{clientPublishDate(task)}</span></div>}
    </div>
  );
}

// Read-only Kanban Column
function KanbanColumn({ status, color, tasks, onView }) {
  const columnTasks = tasks.filter(t => t.status === status);
  return (
    <div className="soft-card overflow-hidden">
      <div className={`px-4 py-3 border-b-4 ${color}`}><h3 className="font-bold text-[#1a1a2e] dark:text-white text-base">{status}</h3><p className="text-sm text-neutral-500">{columnTasks.length} tasks</p></div>
      <div className="p-3 space-y-3 min-h-[400px] overflow-y-auto max-h-[calc(100vh-400px)] bg-white/40 dark:bg-white/5">
        {columnTasks.map(task => <KanbanCard key={task._id} task={task} onView={onView} />)}
        {columnTasks.length === 0 && (
          <EmptyState icon={CheckCircle} title="No tasks" size="small" testId={`empty-kanban-${status.toLowerCase().replace(/\s+/g, '-')}`} />
        )}
      </div>
    </div>
  );
}

// Read-only Calendar View
function TaskCalendarView({ tasks, onView }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startingDay = firstDayOfMonth.getDay();
  const totalDays = lastDayOfMonth.getDate();
  const today = new Date(); today.setHours(0,0,0,0);
  const getTasksForDate = (day) => { const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`; return tasks.filter(t => clientPublishDate(t) === dateStr); };
  const statusColors = { 'New': 'bg-gray-400', 'In Progress': 'bg-blue-500', 'Review': 'bg-purple-500', 'Revision': 'bg-yellow-500', 'Completed': 'bg-green-500' };
  const calendarDays = []; for (let i = 0; i < startingDay; i++) calendarDays.push(null); for (let day = 1; day <= totalDays; day++) calendarDays.push(day);

  return (
    <div className="glass-card p-6" data-testid="task-calendar-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-[#1a1a2e] dark:text-white">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} className="p-2 hover:bg-neutral-100 dark:hover:bg-white/10 rounded-xl"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-xl">Today</button>
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} className="p-2 hover:bg-neutral-100 dark:hover:bg-white/10 rounded-xl"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-neutral-200 dark:bg-neutral-700 rounded-xl overflow-hidden">
        {dayNames.map(d => <div key={d} className="bg-white/40 dark:bg-[#1a1a1a] text-center py-3 text-xs font-semibold text-neutral-600 dark:text-neutral-400">{d}</div>)}
        {calendarDays.map((day, i) => {
          const dayTasks = day ? getTasksForDate(day) : [];
          const isToday = day && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear() && day === today.getDate();
          return (
            <div key={i} className={`bg-white dark:bg-[#1a1a1a] min-h-[100px] p-2 ${isToday ? 'ring-2 ring-inset ring-blue-500' : ''}`}>
              {day && <>
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-neutral-700 dark:text-neutral-300'}`}>{day}</div>
                <div className="space-y-1">
                  {dayTasks.slice(0, 3).map(task => (
                    <div key={task._id} onClick={() => onView(task)} className={`${statusColors[task.status]} text-white text-xs px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80`}>{task.title}</div>
                  ))}
                  {dayTasks.length > 3 && <div className="text-xs text-neutral-500 font-medium">+{dayTasks.length - 3} more</div>}
                </div>
              </>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// View-only Task Detail Modal
function TaskDetailModal({ task, isOpen, onClose }) {
  if (!isOpen || !task) return null;
  const statusColors = { 'New': 'bg-gray-100 text-gray-700', 'In Progress': 'bg-blue-100 text-blue-700', 'Review': 'bg-purple-100 text-purple-700', 'Revision': 'bg-yellow-100 text-yellow-700', 'Completed': 'bg-green-100 text-green-700' };
  const priorityColors = { 'Low': 'bg-gray-100 text-gray-700', 'Medium': 'bg-blue-100 text-blue-700', 'High': 'bg-orange-100 text-orange-700', 'Critical': 'bg-red-100 text-red-700' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="task-detail-modal">
      <div className="bg-white dark:bg-[#1e1e1e] rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1a1a2e] dark:text-white pr-4">{task.title}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl font-bold" data-testid="close-task-detail">&times;</button>
        </div>
        {task.description && <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">{task.description}</p>}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-neutral-500 block text-xs mb-0.5">Status</span><span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[task.status] || ''}`}>{task.status}</span></div>
          <div><span className="text-neutral-500 block text-xs mb-0.5">Priority</span><span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${priorityColors[task.priority] || ''}`}>{task.priority}</span></div>
          <div><span className="text-neutral-500 block text-xs mb-0.5" title="When the content goes live">Publish Date</span><span className="text-[#1a1a2e] dark:text-white font-medium">{clientPublishDate(task) || 'Not set'}</span></div>
          <div><span className="text-neutral-500 block text-xs mb-0.5">Assignee</span><span className="text-[#1a1a2e] dark:text-white font-medium">{task.assignee_name || 'Unassigned'}</span></div>
          {task.task_type && <div><span className="text-neutral-500 block text-xs mb-0.5">Type</span><span className="text-[#1a1a2e] dark:text-white font-medium">{task.task_type}</span></div>}
          {task.department && <div><span className="text-neutral-500 block text-xs mb-0.5">Department</span><span className="text-[#1a1a2e] dark:text-white font-medium">{task.department}</span></div>}
        </div>
        <div className="mt-4 pt-3 border-t border-neutral-200 dark:border-white/10 flex items-center gap-2 text-xs text-neutral-400">
          <Eye className="w-3.5 h-3.5" /> View-only access
        </div>
      </div>
    </div>
  );
}

export default function ClientTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('list');
  const [viewingTask, setViewingTask] = useState(null);
  const [filters, setFilters] = useState({
    clients: [], departments: [], employees: [], taskTypes: [],
    subtypes: [], statuses: [], priorities: [], dateFrom: '', dateTo: ''
  });

  useEffect(() => { fetchTasks(); fetchTaskTypes(); fetchDepartments(); fetchEmployees(); }, []);

  const fetchTasks = async () => {
    try {
      const r = await clientApi.get('/api/v1/tasks', { params: { page: 1, page_size: 200 } });
      setTasks(r.data?.items || []);
    } catch {
      try { const fallback = await clientApi.get('/api/client/tasks'); setTasks(fallback.data || []); } catch {}
    }
  };
  const fetchTaskTypes = async () => { try { const r = await clientApi.get('/api/task-types'); setTaskTypes(r.data || []); } catch {} };
  const fetchDepartments = async () => { try { const r = await clientApi.get('/api/departments'); setDepartments(r.data || []); } catch {} };
  const fetchEmployees = async () => { try { const r = await clientApi.get('/api/client/team'); setEmployees(r.data || []); } catch {} };

  const statusColors = { 'New': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200', 'In Progress': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', 'Review': 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', 'Revision': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300', 'Completed': 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' };
  const priorityColors = { 'Low': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300', 'Medium': 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300', 'High': 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300', 'Critical': 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300' };
  const kanbanColumns = [{ status: 'New', color: 'border-gray-400' }, { status: 'In Progress', color: 'border-blue-500' }, { status: 'Review', color: 'border-purple-500' }, { status: 'Revision', color: 'border-yellow-500' }, { status: 'Completed', color: 'border-green-500' }];

  const filteredTasks = tasks.filter(t => {
    if (searchQuery && !t.title?.toLowerCase().includes(searchQuery.toLowerCase()) && !t.description?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filters.departments.length > 0 && !filters.departments.includes(t.department)) return false;
    if (filters.employees.length > 0 && !filters.employees.includes(t.primary_assignee_id)) return false;
    if (filters.taskTypes.length > 0 && !filters.taskTypes.includes(t.task_type)) return false;
    if (filters.subtypes.length > 0 && !filters.subtypes.includes(t.task_subtype)) return false;
    if (filters.statuses.length > 0 && !filters.statuses.includes(t.status)) return false;
    if (filters.priorities.length > 0 && !filters.priorities.includes(t.priority)) return false;
    const pd = clientPublishDate(t);
    if (filters.dateFrom && pd && pd < filters.dateFrom) return false;
    if (filters.dateTo && pd && pd > filters.dateTo) return false;
    return true;
  });

  return (
    <div className="p-8" data-testid="client-tasks-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e] dark:text-white">Tasks</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">View and track your project tasks</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-400 bg-neutral-100 dark:bg-white/5 px-3 py-1.5 rounded-full" data-testid="view-only-badge">
          <Eye className="w-3.5 h-3.5" /> View Only
        </div>
      </div>

      <TaskStatistics tasks={tasks} />

      <div className="flex items-start gap-4 mb-6">
        <div className="flex-1">
          <div className="relative mb-3">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input type="text" placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-white/40 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-400/30 bg-white dark:bg-white/5 text-[#1a1a2e] dark:text-white" data-testid="search-tasks" />
          </div>
          <TaskFilters filters={filters} onFiltersChange={setFilters} clients={[]} departments={departments} employees={employees} taskTypes={taskTypes} showClientFilter={false} accentColor="blue" />
        </div>
        <div className="flex items-center gap-1 glass-card rounded-xl p-1" data-testid="view-toggle">
          {['list', 'kanban', 'calendar'].map(v => (
            <button key={v} onClick={() => setActiveView(v)} data-testid={`view-${v}`}
              className={`px-4 py-2 rounded-xl flex items-center gap-2 ${activeView === v ? 'bg-gradient-to-r from-[#3366FF] to-[#3366FF] text-white shadow-soft' : 'text-neutral-700 dark:text-neutral-300 hover:bg-white/60 dark:hover:bg-white/10'}`}>
              {v === 'list' && <List className="w-4 h-4" />}{v === 'kanban' && <LayoutGrid className="w-4 h-4" />}{v === 'calendar' && <CalendarIcon className="w-4 h-4" />}
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="text-sm text-neutral-500 mb-4" data-testid="task-count">Showing {filteredTasks.length} of {tasks.length} tasks</div>

      {activeView === 'list' && (
        <div className="soft-card overflow-hidden" data-testid="task-list-view">
          <table className="w-full">
            <thead className="bg-white/40 dark:bg-white/5 border-b border-white/40 dark:border-white/10">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Task</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Priority</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300" title="When the content goes live">Publish Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Assignee</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30 dark:divide-white/5">
              {filteredTasks.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-2">
                  <EmptyState icon={CheckCircle} title="No tasks found" description="Try adjusting your filters." testId="empty-client-tasks-table" />
                </td></tr>
              ) : (
                filteredTasks.map(task => <TaskRow key={task._id} task={task} onView={setViewingTask} statusColors={statusColors} priorityColors={priorityColors} />)
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeView === 'kanban' && (
        <div className="grid grid-cols-5 gap-4" data-testid="task-kanban-view">
          {kanbanColumns.map(col => <KanbanColumn key={col.status} status={col.status} color={col.color} tasks={filteredTasks} onView={setViewingTask} />)}
        </div>
      )}

      {activeView === 'calendar' && <TaskCalendarView tasks={filteredTasks} onView={setViewingTask} />}

      <TaskDetailModal task={viewingTask} isOpen={!!viewingTask} onClose={() => setViewingTask(null)} />
    </div>
  );
}
